import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { formatTime } from './format'

const HOUR12_KEY = 'bizzey:booker:hour12'

export function useHour12() {
  const [hour12, setHour12] = React.useState<boolean>(() => {
    try { const v = window.localStorage.getItem(HOUR12_KEY); if (v !== null) return v === '1' } catch {}
    try { return new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions().hour12 ?? false } catch { return false }
  })
  const update = (value: boolean) => {
    setHour12(value)
    try { window.localStorage.setItem(HOUR12_KEY, value ? '1' : '0') } catch {}
  }
  return [hour12, update] as const
}

interface IProps {
  heading: React.ReactNode
  slots: string[]
  timezone: string
  hour12: boolean
  onHour12Change: (value: boolean) => void
  onSelect: (iso: string) => void
  loading?: boolean
  wide?: boolean
}

// Segmented 12h/24h switch, styled after cal.com's booker
export const SEGMENTED_GROUP = 'gap-0.5 rounded-lg bg-muted p-0.5'
export const SEGMENTED_ITEM = 'h-7 min-w-0 rounded-md border border-transparent px-1.5 text-sm text-foreground/80 hover:bg-transparent aria-pressed:border-border aria-pressed:bg-background aria-pressed:text-foreground aria-pressed:shadow-xs'

export default function TimeSlots({ heading, slots, timezone, hour12, onHour12Change, onSelect, loading, wide }: IProps) {
  const { t, i18n } = useTranslation()

  return (
    <div className={cn('flex flex-col gap-3', wide && 'h-full min-h-0')}>
      <div className='flex items-center justify-between gap-2'>
        <div className='text-base font-semibold text-foreground'>{heading}</div>
        <ToggleGroup value={[hour12 ? '12h' : '24h']} onValueChange={(v: string[]) => v[0] && onHour12Change(v[0] === '12h')} size='sm' className={SEGMENTED_GROUP} aria-label={t('Scheduling::Time format')}>
          <ToggleGroupItem value='12h' className={SEGMENTED_ITEM}>{t('Scheduling::12h')}</ToggleGroupItem>
          <ToggleGroupItem value='24h' className={SEGMENTED_ITEM}>{t('Scheduling::24h')}</ToggleGroupItem>
        </ToggleGroup>
      </div>
      {loading ? (
        <div className='flex flex-col gap-2' aria-busy='true'>
          {[0, 1, 2, 3].map(i => <Skeleton key={i} className='h-9 w-full' />)}
        </div>
      ) : slots.length === 0 ? (
        <p className='text-sm text-muted-foreground'>{t('Scheduling::No available times on this day')}</p>
      ) : (
        <ul className={cn('flex flex-col gap-1.5', wide && 'min-h-0 flex-1 overflow-y-auto pb-3')}>
          {slots.map(slot => (
            <li key={slot}>
              <Button variant='outline' className='h-9 w-full gap-2 rounded-lg border-input text-sm font-medium hover:border-foreground hover:bg-background' onClick={() => onSelect(slot)}>
                <span className='size-2 rounded-full bg-emerald-500' aria-hidden='true' />
                {formatTime(slot, timezone, hour12, i18n.language)}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
